import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaStar, FaArrowLeft, FaPhoneAlt, FaEnvelope, FaCommentDots } from 'react-icons/fa';

import { END_POINT } from '../../Redux/AdminReducer/action';
import { Badge, Button } from '../../Components/UI';
import exportToExcel from '../../UtilityHookFunctions/exportToExcel';

const PREVIEW_LENGTH = 180;

// Only fields that were actually filled in are shown; feedback from the public
// patient link carries just the basics, so most of these are usually blank.
const DETAIL_FIELDS = [
  ['Case No', 'CaseNo'],
  ['Status', 'Status'],
  ['Date', 'Date'],
  ['Date of Birth', 'dateOfBirth'],
  ['Gender', 'gender'],
  ['Blood Group', 'bloodGroup'],
  ['Marital Status', 'maritalStatus'],
  ['Mother Tongue', 'motherTongue'],
  ['Religion', 'religion'],
  ['Education', 'education'],
  ['Profession', 'profession'],
  ['Industry', 'industry'],
  ['State', 'state'],
  ['Address', 'address'],
];

const formatDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? value
    : date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const Stars = ({ rating = 0, size = 'h-4 w-4' }) => (
  <div className="flex items-center gap-0.5" aria-label={`${rating} out of 5`}>
    {[...Array(5)].map((_, index) => (
      <FaStar key={index} className={`${size} ${index < rating ? 'text-amber-400' : 'text-slate-200'}`} />
    ))}
  </div>
);

const ratingVariant = (rating) => (rating >= 4 ? 'success' : rating >= 3 ? 'warning' : 'danger');

const CommentCard = ({ comment }) => {
  const [expanded, setExpanded] = useState(false);
  const content = String(comment?.content || '');
  const isLong = content.length > PREVIEW_LENGTH;

  return (
    <article className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-3">
          <Stars rating={comment?.rating} />
          {comment?.rating ? (
            <Badge size="sm" variant={ratingVariant(comment.rating)}>
              {comment.rating} / 5
            </Badge>
          ) : null}
        </div>
        {comment?.date && <span className="text-xs font-medium text-slate-500">{formatDate(comment.date)}</span>}
      </div>

      <p className="mt-3 whitespace-pre-line break-words text-sm leading-relaxed text-slate-700">
        {content ? (isLong && !expanded ? `${content.slice(0, PREVIEW_LENGTH)}…` : content) : (
          <span className="italic text-slate-400">No comment written</span>
        )}
      </p>
      {isLong && (
        <button type="button" onClick={() => setExpanded((prev) => !prev)} className="mt-1 text-xs font-semibold text-sky-600 hover:text-sky-700">
          {expanded ? 'Show less' : 'Read more'}
        </button>
      )}

      {comment?.signature && (
        <p className="mt-3 border-t border-slate-100 pt-2 text-xs text-slate-500">
          Signed by <span className="font-semibold text-slate-700">{comment.signature}</span>
        </p>
      )}
    </article>
  );
};

const FeedbackList = () => {
  const { feedback_id } = useParams();
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios
      .get(`${END_POINT}/feedback/${feedback_id}`)
      .then((res) => active && setFeedback(res?.data || null))
      .catch((error) => {
        console.log(error);
        if (active) {
          setFeedback(null);
          toast.error(error?.response?.data?.error || 'Could not load feedback');
        }
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [feedback_id]);

  const comments = useMemo(() => {
    const list = Array.isArray(feedback?.comments) ? [...feedback.comments] : [];
    return list.sort((a, b) => String(b?.date || '').localeCompare(String(a?.date || '')));
  }, [feedback]);

  const rated = comments.filter((comment) => Number(comment?.rating) > 0);
  const averageRating = rated.length ? rated.reduce((sum, comment) => sum + Number(comment.rating), 0) / rated.length : 0;

  const name = [feedback?.FirstName, feedback?.MiddleName, feedback?.LastName].filter(Boolean).join(' ') || 'Unnamed patient';
  const details = DETAIL_FIELDS.filter(([, key]) => feedback?.[key]);
  const complaints = (feedback?.complaints || []).filter((complaint) => complaint?.content);

  return (
    <div className="w-full min-h-screen bg-slate-50 p-4 sm:p-6">
      <div className="mx-auto flex max-w-5xl flex-col gap-5">
        <div>
          <Button variant="ghost" size="sm" icon={<FaArrowLeft />} onClick={() => navigate(-1)}>
            Back to feedback
          </Button>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">Loading feedback…</div>
        ) : !feedback ? (
          <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">Feedback not found.</div>
        ) : (
          <>
            <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full bg-sky-100 text-xl font-bold text-sky-700">
                    {name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <h1 className="truncate text-xl font-semibold text-slate-900">{name}</h1>
                    <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                      {feedback.mobile && (
                        <a href={`tel:${feedback.mobile}`} className="inline-flex items-center gap-1.5 hover:text-sky-700">
                          <FaPhoneAlt className="h-3 w-3" /> {feedback.mobile}
                        </a>
                      )}
                      {feedback.email && (
                        <a href={`mailto:${feedback.email}`} className="inline-flex items-center gap-1.5 hover:text-sky-700">
                          <FaEnvelope className="h-3 w-3" /> {feedback.email}
                        </a>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-center">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Avg rating</p>
                    <div className="mt-1 flex items-center justify-center gap-2">
                      <span className="text-lg font-bold text-slate-900">{averageRating ? averageRating.toFixed(1) : '–'}</span>
                      <FaStar className="h-4 w-4 text-amber-400" />
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-2 text-center">
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Feedback</p>
                    <p className="mt-1 text-lg font-bold text-slate-900">{comments.length}</p>
                  </div>
                </div>
              </div>

              {(details.length > 0 || complaints.length > 0) && (
                <div className="mt-5 grid gap-5 border-t border-slate-100 pt-5 md:grid-cols-3">
                  {details.length > 0 && (
                    <dl className="grid grid-cols-2 gap-x-6 gap-y-3 md:col-span-2">
                      {details.map(([label, key]) => (
                        <div key={key} className="min-w-0">
                          <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</dt>
                          <dd className="mt-0.5 break-words text-sm text-slate-800">{key === 'dateOfBirth' || key === 'Date' ? formatDate(feedback[key]) : feedback[key]}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                  {complaints.length > 0 && (
                    <div>
                      <h3 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Complaints</h3>
                      <ul className="mt-2 flex flex-wrap gap-2">
                        {complaints.map((complaint, index) => (
                          <li key={complaint?._id || index}>
                            <Badge variant="danger">{complaint.content}</Badge>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </section>

            <section className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-slate-900">Feedback history</h2>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-slate-500">Newest first</span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={!comments.length}
                    onClick={() =>
                      exportToExcel(comments, `Feedback_${name}`, [
                        { key: 'date', label: 'Date', format: (value) => formatDate(value) },
                        { key: 'rating', label: 'Rating' },
                        { key: 'content', label: 'Feedback' },
                        { key: 'signature', label: 'Signed By' },
                      ])
                    }
                  >
                    Export to Excel
                  </Button>
                </div>
              </div>

              {comments.length ? (
                comments.map((comment, index) => <CommentCard key={comment?._id || index} comment={comment} />)
              ) : (
                <div className="flex flex-col items-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
                  <FaCommentDots className="h-6 w-6 text-slate-300" />
                  <p className="text-sm text-slate-500">No feedback submitted yet.</p>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default FeedbackList;
